import { ChangeDetectionStrategy, Component, DestroyRef, inject, Input, OnInit, ViewChild } from '@angular/core';
import { ScreenDTOEnhanced } from '../../../pages/network/device-details/device-details-screens/device-details-screens.component';
import { API, TemplateGroupDTO, TemplateScreenEntryWithDataSetDTO, TemplateVariableType } from '../../../services/api/api';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, take } from 'rxjs/operators';
import { ErrorService } from '../../../services/error.service';
import { SbDateTime, SbLocalDateTime } from '../../../services/api/api-date-time';
import { PermissionService } from '../../../services/permission.service';
import { RequestClass } from '../../../services/global/RequestClass';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { FilterPanelComponent, FilterPanelConfig } from '@ngx-seatback/layout';
import { StringTransformationService } from '../../../services/transformation-helper/string-transformation.service';
import { SbSplitButton } from '../../../services/global/global-interfaces';

type SplitButtonAction = 'approve' | 'reject';

@Component({
  selector: 'sb-comp-rendered-templates',
  templateUrl: './rendered-templates.component.html',
  styleUrls: ['./rendered-templates.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RenderedTemplatesComponent extends RequestClass implements OnInit {
  protected readonly TemplateVariableType = TemplateVariableType;
  protected readonly permissionService = inject(PermissionService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly api = inject(API);
  private readonly errorHandler = inject(ErrorService);
  private readonly stringTransformation = inject(StringTransformationService);

  @Input({ required: true }) screen: ScreenDTOEnhanced;
  @Input({ required: true }) group: TemplateGroupDTO;

  @ViewChild('FilterPanelRef') filterPanelRef: FilterPanelComponent;

  // table wrapper variables
  renderedTemplates: TemplateScreenEntryWithDataSetDTO[] = [];

  // filter variables
  exactMatch: SearchOptions = 'contains';
  search: string | null = null;
  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    button: { visibleWithoutChanges: true },
  };
  from: SbLocalDateTime = undefined;
  till: SbLocalDateTime = undefined;

  ngOnInit() {
    if (!this.screen) {
      throw new Error('No Screen Provided');
    }
    if (!this.group) {
      throw new Error('No Group Provided');
    }

    this.refreshFromServer();
  }

  refreshFromServer() {
    if (!this.pagination) {
      this.loading.set(false);
      return;
    }
    const sub = this.api
      .ss_nm_Screen_retrieveTemplateScreenEntriesByScreen(
        this.screen.id,
        {
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
          validFrom: this.from,
          validTill: this.till,
          group: this.group ? [this.group.id] : null,
        },
        this.pagination
      )
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: templates => {
          this.renderedTemplates = this.buildSplitButtons(templates);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  handleDateChange(dates: { fromDate: SbDateTime; toDate: SbDateTime }) {
    if (!dates) {
      this.from = null;
      this.till = null;
    }
    if (!dates?.fromDate || !dates?.toDate) {
      return;
    }
    this.from = new SbLocalDateTime(dates.fromDate.value);
    this.till = new SbLocalDateTime(dates.toDate.value);
    this.requestDebounced();
  }

  pullRejectionFromEntry(entry: TemplateScreenEntryWithDataSetDTO) {
    if (!entry?.id) {
      throw new Error('Missing template screen entry');
    }
    this.api
      .ss_nm_Screen_pullRejectionFromTemplateScreenEntryById(entry.id)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.requestDebounced();
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  setRejectionOfEntry(entry: TemplateScreenEntryWithDataSetDTO) {
    if (!entry?.id) {
      throw new Error('Missing template screen entry');
    }
    this.api
      .ss_nm_Screen_rejectTemplateScreenEntryById(entry.id)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.requestDebounced();
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  openFilterPanel(elementId: HTMLDivElement) {
    if (!elementId) {
      throw new Error('Missing elementId for filter panel opening');
    }
    if (this.filterPanelRef) {
      this.filterPanelRef.isPanelExtended.set(true);
      this.scrollToFilterPanelElement(elementId, false);
    }
  }

  scrollToFilterPanelElement(element: HTMLDivElement, isInitiallyExpanded: boolean = true) {
    if (!element) {
      throw new Error('Missing elementId for filter panel opening');
    }
    const timeout = setTimeout(
      () => {
        const scrollContainer = element.closest('.custom-scrollbar') as HTMLElement;

        if (!scrollContainer) {
          console.warn('No scrollable container found for element');
          return;
        }
        const containerTop = scrollContainer.getBoundingClientRect().top;
        const elementTop = element.getBoundingClientRect().top;

        const headerOffset = 110; // height filter header + height of navbar + offset
        const offsetPosition = elementTop - containerTop + scrollContainer.scrollTop - headerOffset;

        scrollContainer.scrollTo({ behavior: 'smooth', top: offsetPosition });
        // elementId.scrollBy({ top: 40 });
        clearTimeout(timeout);
      },
      isInitiallyExpanded ? 100 : 400
    );
  }

  checkExpansion() {
    return this.filterPanelRef?.isPanelExtended() ?? false;
  }

  buildSplitButtons<T extends { id: any }>(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      if (this.permissionService.hasPermissions([this.permissionService.Permission.template_screen_entry_delete])) {
        item.sbButtons.push({
          icon: 'boxChecked',
          iconClass: 'hover:!tw-bg-sbGreen-500 *:tw-text-sbGreen-500',
          action: 'approve',
          tooltip: 'lang.globals.approve',
        });
        item.sbButtons.push({
          icon: 'error',
          iconClass: 'hover:!tw-bg-sbRed-500 *:tw-text-sbRed-500',
          action: 'reject',
          tooltip: 'lang.globals.reject',
        });
      }
    });
    return result;
  }

  resolveSplitButtonAction(template: TemplateScreenEntryWithDataSetDTO, $event: SplitButtonAction) {
    switch ($event) {
      case 'approve':
        this.pullRejectionFromEntry(template);
        break;
      case 'reject':
        this.setRejectionOfEntry(template);
        break;
      default:
        console.error('Missing action implementation of Splitbutton');
        break;
    }
  }

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
  }
}
